import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, Loader2, ArrowRight, AlertTriangle } from "lucide-react";

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | success | failed | timeout
  const [planId, setPlanId] = useState("");
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setPlanId(data.plan_id || "");
        if (data.payment_status === "paid") {
          setStatus("success");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        if (attempts >= maxAttempts) {
          setStatus("timeout");
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        setStatus("failed");
      }
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="success-page">
      <div className="max-w-md w-full card-surface p-10 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="animate-spin mx-auto mb-6 text-zinc-400" size={32} />
            <h1 className="font-display text-3xl mb-2">Confirming payment</h1>
            <p className="text-zinc-500">Hang tight—this takes a few seconds.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="mx-auto mb-6 text-white" size={40} />
            <div className="overline mb-2">Payment received</div>
            <h1 className="font-display text-3xl mb-3">You're in.</h1>
            <p className="text-zinc-400 mb-8">
              {planId ? `Your ${planId.charAt(0).toUpperCase() + planId.slice(1)} access is active.` : "Your access is active."}
            </p>
            <Link to="/dashboard" className="btn-primary justify-center w-full" data-testid="success-go-dashboard">
              Open dashboard <ArrowRight size={16} />
            </Link>
          </>
        )}
        {status === "failed" && (
          <>
            <AlertTriangle className="mx-auto mb-6 text-zinc-400" size={32} />
            <h1 className="font-display text-3xl mb-2">Payment not completed</h1>
            <p className="text-zinc-500 mb-8">Something went wrong. Try again or contact support.</p>
            <Link to="/" className="btn-secondary justify-center w-full" data-testid="success-back-home">
              Back to home
            </Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <AlertTriangle className="mx-auto mb-6 text-zinc-400" size={32} />
            <h1 className="font-display text-3xl mb-2">Still processing</h1>
            <p className="text-zinc-500 mb-8">We'll email you once it's confirmed.</p>
            <Link to="/dashboard" className="btn-secondary justify-center w-full" data-testid="success-go-dashboard">
              Go to dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
